import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authApi, setActiveWorkspace as setApiActiveWorkspace, getActiveWorkspace, wasWorkspaceRecentlySwitched } from '../lib/api';

import { toast } from 'sonner';

const isSuspended = (w) => ['suspended', 'archived', 'deleted'].includes(String(w?.status || '').toLowerCase());
const pickDefaultWorkspace = (list, tenantId) => {
  const usable = list.filter((w) => !isSuspended(w));
  return usable.find((w) => w.id === tenantId) || usable[0] || list[0];
};

const AuthContext = createContext(null);

// RBAC Permission Matrix based on Meridian RBAC Document
const PERMISSIONS = {
  // USER & WORKSPACE MANAGEMENT
  invite_users: ['client_admin', 'system_admin'],
  manage_user_roles: ['client_admin', 'system_admin'],
  deactivate_users: ['client_admin', 'system_admin'],
  view_user_activity: ['client_admin', 'system_admin'],
  update_workspace_settings: ['client_admin', 'system_admin'],
  configure_evidence_templates: ['client_admin', 'meridian_operator', 'meridian_approver', 'system_admin'],
  
  // DATA IMPORT WORKFLOW
  upload_csv: ['client_admin', 'meridian_operator', 'meridian_approver', 'system_admin'],
  view_staged_imports: ['client_admin', 'meridian_operator', 'meridian_approver', 'system_admin'],
  review_import_errors: ['client_admin', 'meridian_operator', 'meridian_approver', 'system_admin'],
  validate_map_csv: ['meridian_operator', 'system_admin'],
  formally_import_data: ['meridian_operator', 'system_admin'],
  view_import_audit: ['client_admin', 'meridian_operator', 'meridian_approver', 'system_admin'],
  
  // CASE MANAGEMENT
  view_cases: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', 'system_admin'],
  view_case_detail: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', 'system_admin'],
  create_case: ['meridian_operator', 'system_admin'],
  edit_case: ['meridian_operator', 'system_admin'],
  move_case_stage: ['meridian_operator', 'system_admin'],
  flag_exception: ['meridian_operator', 'system_admin'],
  view_exceptions: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', 'system_admin'],
  
  // EVIDENCE PACK MANAGEMENT
  view_evidence: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', '3pl_operator', 'system_admin'],
  upload_evidence: ['meridian_operator', '3pl_operator', 'system_admin'],
  upload_3point_photos: ['meridian_operator', '3pl_operator', 'system_admin'],
  edit_delete_evidence: ['meridian_operator', 'system_admin'],
  view_evidence_history: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', '3pl_operator', 'system_admin'],
  export_evidence_pack: ['meridian_operator', 'meridian_approver', 'system_admin'],
  download_evidence_client: ['client_admin', 'viewer', 'system_admin'],
  
  // CASE APPROVAL & FILING
  view_ready_approval: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', 'system_admin'],
  approve_case: ['meridian_approver', 'system_admin'],
  reject_case: ['meridian_approver', 'system_admin'],
  record_decision: ['meridian_approver', 'system_admin'],
  view_decision_log: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', 'system_admin'],
  generate_filing_dataset: ['meridian_approver', 'system_admin'],
  lock_case: ['meridian_approver', 'system_admin'],
  
  // FILING & HMRC SUBMISSION
  view_filing_status: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', 'system_admin'],
  view_hmrc_rejection: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', 'system_admin'],
  resubmit_filing: ['meridian_approver', 'system_admin'],
  
  // DASHBOARDS & REPORTING
  view_executive_snapshot: ['client_admin', 'viewer', 'meridian_approver', 'system_admin'],
  view_compliance_panel: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', 'system_admin'],
  view_finance_roi: ['client_admin', 'viewer', 'meridian_approver', 'system_admin'],
  view_operational_drilldown: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', 'system_admin'],
  export_reports: ['client_admin', 'viewer', 'meridian_approver', 'system_admin'],
  download_finance_pack: ['client_admin', 'viewer', 'meridian_approver', 'system_admin'],
  
  // IMPORTS & MRN MATCHING
  upload_returns_csv: ['client_admin', 'system_admin'],
  upload_exports_csv: ['meridian_operator', 'meridian_approver', 'system_admin'],
  run_automated_matching: ['meridian_operator', 'meridian_approver', 'system_admin'],
  review_matches: ['meridian_operator', 'meridian_approver', 'system_admin'],
  apply_matches: ['meridian_operator', 'meridian_approver', 'system_admin'],
  view_matching_status: ['client_admin', 'viewer', 'system_admin'],  // Read-only view of matching stats
  
  // COMMUNICATION & COLLABORATION
  post_comments: ['client_admin', 'meridian_operator', 'meridian_approver', '3pl_operator', 'system_admin'],
  assign_tasks: ['meridian_operator', 'system_admin'],
  receive_notifications: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', '3pl_operator', 'system_admin'],
  
  // AUDIT & COMPLIANCE
  view_full_audit: ['client_admin', 'meridian_operator', 'meridian_approver', 'system_admin'],
  view_case_decision_history: ['client_admin', 'viewer', 'meridian_operator', 'meridian_approver', 'system_admin'],
  view_import_audit_trail: ['client_admin', 'meridian_operator', 'meridian_approver', 'system_admin'],
  export_audit_log: ['meridian_approver'],
  
  // INTAKE & WAREHOUSE (3PL-SPECIFIC)
  confirm_return_receipt: ['3pl_operator'],
  scan_barcode: ['3pl_operator'],
  enter_condition_grade: ['3pl_operator'],
  upload_intake_photos: ['3pl_operator'],
  attach_carrier_docs: ['3pl_operator'],
  view_intake_status: ['meridian_operator', '3pl_operator'],
  view_financial_data: ['meridian_operator', 'meridian_approver', '3pl_operator'],
  
  // SYSTEM ADMIN (Global permissions)
  manage_workspaces: ['system_admin'],
  create_workspace: ['system_admin'],
  suspend_workspace: ['system_admin'],
  manage_all_users: ['system_admin'],
  view_global_audit: ['system_admin'],
  configure_security: ['system_admin'],
  view_platform_stats: ['system_admin'],
  impersonate_user: ['system_admin'],
  manage_integrations: ['system_admin'],
  
  // Enhanced Approver permissions
  bulk_approve: ['meridian_approver'],
  generate_filing_dataset: ['meridian_approver'],
  view_approvals_queue: ['meridian_approver'],
  
  // Tasks & Comments
  create_tasks: ['meridian_operator', 'meridian_approver'],
  view_tasks: ['client_admin', 'meridian_operator', 'meridian_approver', '3pl_operator'],
  complete_tasks: ['client_admin', 'meridian_operator', 'meridian_approver', '3pl_operator'],
  post_internal_comments: ['meridian_operator', 'meridian_approver'],
  
  // Client Admin enhanced
  manage_workspace_users: ['client_admin'],
  view_workspace_settings: ['client_admin'],
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
  const [lastWorkspaceSwitchTime, setLastWorkspaceSwitchTime] = useState(null);
  // Iter S25 — retain the last-set workspace id across renders so the
  // ``enterMerchantWorkspace`` helper can decide whether to bump the
  // switch timestamp without pulling ``activeWorkspace`` into its
  // useCallback dep array (which would re-create the fn every switch).
  const previousWorkspaceIdRef = React.useRef(null);
  // Iter263 Phase R3-1 — partner cross-workspace mode is a distinct
  // discriminator from sysadmin cross-mode. Every consumer that needs
  // to know "are we in All Workspaces" for a partner user reads this.
  const [isPartnerCrossMode, setIsPartnerCrossMode] = useState(false);

  // Initialize workspace from localStorage
  useEffect(() => {
    const savedWorkspaceId = getActiveWorkspace();
    const crossWorkspaceMode = localStorage.getItem('crossWorkspaceMode') === 'true';
    const partnerCross = localStorage.getItem('partnerCrossWorkspaceMode') === 'true';
    setIsPartnerCrossMode(partnerCross);

    // If cross-workspace mode is enabled (System Admin), don't set a workspace
    if (crossWorkspaceMode || partnerCross) {
      setActiveWorkspace(null);
    } else if (savedWorkspaceId) {
      setActiveWorkspace({ id: savedWorkspaceId });
    }
  }, []);

  // Listen for workspace changes from other components
  useEffect(() => {
    const handleWorkspaceChange = (event) => {
      const { workspaceId } = event.detail || {};
      if (workspaceId) setActiveWorkspace({ id: workspaceId });
    };
    // Iter263 Phase R3-1 — partner switcher + partner drill-in fire
    // this so AuthContext / TenantChip / Header stay in sync with the
    // localStorage source of truth.
    const handlePartnerModeChange = () => {
      const cross = localStorage.getItem('partnerCrossWorkspaceMode') === 'true';
      setIsPartnerCrossMode(cross);
      if (cross) {
        setActiveWorkspace(null);
      } else {
        const ws = localStorage.getItem('activeWorkspaceId');
        if (ws) setActiveWorkspace({ id: ws });
      }
    };

    window.addEventListener('workspaceChanged', handleWorkspaceChange);
    window.addEventListener('partnerModeChanged', handlePartnerModeChange);
    return () => {
      window.removeEventListener('workspaceChanged', handleWorkspaceChange);
      window.removeEventListener('partnerModeChanged', handlePartnerModeChange);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    // iter216 — HttpOnly cookie migration: new logins don't stash the
    // token in localStorage, but the `access_token` cookie still
    // carries the session. Boot if EITHER (legacy token+user) OR
    // (savedUser only, cookie assumed) is present. `authApi.getMe()`
    // will 401 if the cookie is gone, and we clear state in .catch().
    const hasSession = (token && savedUser) || savedUser;

    if (hasSession) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      // iter224.2 — respect an explicit Global (All Workspaces) selection.
      // Sysadmin can toggle `crossWorkspaceMode='true'`, which means the
      // active workspace MUST stay unset. Previously we clobbered it with
      // `parsedUser.tenant_id` on every reload, breaking direct-navigation
      // to workspace-scoped pages (they'd re-hydrate a stale tenant ID
      // while the switcher still displayed Global).
      const globalMode = localStorage.getItem('crossWorkspaceMode') === 'true'
        && parsedUser.role === 'system_admin';
      if (globalMode) {
        setApiActiveWorkspace(null);
        setActiveWorkspace(null);
      } else if (!getActiveWorkspace() && parsedUser.tenant_id) {
        setApiActiveWorkspace(parsedUser.tenant_id);
        setActiveWorkspace({ id: parsedUser.tenant_id });
      }

      // Verify token is still valid and fetch workspaces
      authApi.getMe()
        .then(async (res) => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));

          // Fetch available workspaces
          try {
            const wsRes = await authApi.getMyWorkspaces();
            setAvailableWorkspaces(wsRes.data);

            // iter224.2 — if Global mode is active, keep it that way.
            const stillGlobal = localStorage.getItem('crossWorkspaceMode') === 'true'
              && res.data.role === 'system_admin';
            if (stillGlobal) {
              setApiActiveWorkspace(null);
              setActiveWorkspace(null);
            } else {
              // Set active workspace - prioritize existing, then first available, then tenant_id
              const existingActiveId = getActiveWorkspace();
              const hasExistingAccess = existingActiveId && wsRes.data.some(w => w.id === existingActiveId);

              if (hasExistingAccess) {
                // Keep existing workspace if user still has access
                setActiveWorkspace({ id: existingActiveId });
              } else if (wsRes.data.length > 0) {
                const preferred = pickDefaultWorkspace(wsRes.data, res.data.tenant_id);
                setApiActiveWorkspace(preferred.id);
                setActiveWorkspace({ id: preferred.id });
              } else if (res.data.tenant_id) {
                // Fallback to tenant_id
                setApiActiveWorkspace(res.data.tenant_id);
                setActiveWorkspace({ id: res.data.tenant_id });
              }
            }
          } catch (wsErr) {
            console.error('Failed to fetch workspaces:', wsErr);
            // Fallback to tenant_id, unless we're in Global mode.
            const stillGlobal = localStorage.getItem('crossWorkspaceMode') === 'true'
              && res.data.role === 'system_admin';
            if (!stillGlobal && !getActiveWorkspace() && res.data.tenant_id) {
              setApiActiveWorkspace(res.data.tenant_id);
              setActiveWorkspace({ id: res.data.tenant_id });
            }
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('activeWorkspaceId');
          setUser(null);
          setActiveWorkspace(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, mfaCode = null) => {
    const res = await authApi.login({ email, password, mfa_code: mfaCode });
    
    // Check if MFA is required
    if (res.data.mfa_required) {
      // Return MFA pending state for LoginPage to handle
      return {
        mfa_required: true,
        mfa_pending_token: res.data.mfa_pending_token,
        message: res.data.message
      };
    }
    
    const { token, user: userData } = res.data;
    // iter216 — HttpOnly cookie migration. The backend now plants the
    // JWT as a `Secure, HttpOnly` cookie on this same response, so
    // localStorage token storage is no longer required. We keep the
    // `user` blob in localStorage so the boot sequence can render the
    // shell instantly (before `/auth/me` returns), but the token
    // itself never goes to a JS-readable surface. Existing sessions
    // that still have `token` in localStorage from before this rev
    // are handled by the fallback in `api.js`.
    if (token) {
      localStorage.removeItem('token');
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Fetch available workspaces for this user
    try {
      const wsRes = await authApi.getMyWorkspaces();
      setAvailableWorkspaces(wsRes.data);
      
      // System Admin: Default to Global (cross-workspace) view
      if (userData.role === 'system_admin') {
        localStorage.setItem('crossWorkspaceMode', 'true');
        localStorage.removeItem('activeWorkspaceId');
        setActiveWorkspace(null);
        return userData;
      }
      
      // Non-System Admin: Set to first available workspace or tenant_id
      // Filter out system-type workspaces
      const filteredWorkspaces = wsRes.data.filter(w => w.workspace_type !== 'system');
      if (filteredWorkspaces.length > 0) {
        const firstWorkspace = pickDefaultWorkspace(filteredWorkspaces, userData.tenant_id);
        setApiActiveWorkspace(firstWorkspace.id);
        setActiveWorkspace({ id: firstWorkspace.id });
      } else if (userData.tenant_id) {
        setApiActiveWorkspace(userData.tenant_id);
        setActiveWorkspace({ id: userData.tenant_id });
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
      // Fallback to tenant_id if workspace fetch fails (only for non-system-admin)
      if (userData.tenant_id && userData.role !== 'system_admin') {
        setApiActiveWorkspace(userData.tenant_id);
        setActiveWorkspace({ id: userData.tenant_id });
      }
    }
    
    return userData;
  };

  const register = async (data) => {
    const res = await authApi.register(data);
    const { token, user: userData } = res.data;
    // iter216 — see login() above. Cookie is source of truth; do not
    // persist the token in JS-readable storage.
    if (token) {
      localStorage.removeItem('token');
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Set active workspace to user's default tenant
    if (userData.tenant_id) {
      setApiActiveWorkspace(userData.tenant_id);
      setActiveWorkspace({ id: userData.tenant_id });
    }
    
    return userData;
  };

  const logout = async () => {
    // iter215 — call the backend so the httpOnly access_token cookie
    // is cleared. Best-effort — even if the request fails (offline,
    // 401, etc.) we still tear down the local state so the user gets
    // signed out of this browser tab.
    try {
      if (authApi.logout) {
        await authApi.logout();
      }
    } catch (_) {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activeWorkspaceId');
    setUser(null);
    setActiveWorkspace(null);
    setAvailableWorkspaces([]);
  };

  // Switch to a different workspace
  const switchWorkspace = useCallback((workspaceId) => {
    const previousWorkspaceId = getActiveWorkspace();
    
    // Track timestamp if workspace actually changed
    if (previousWorkspaceId && previousWorkspaceId !== workspaceId) {
      setLastWorkspaceSwitchTime(Date.now());
    }
    
    if (workspaceId) {
      setApiActiveWorkspace(workspaceId);
      setActiveWorkspace({ id: workspaceId });
      localStorage.removeItem('crossWorkspaceMode');
      // Iter S25 — clear the partner cross-workspace flag too so a
      // partner sysadmin drilling into a merchant workspace stops
      // seeing X-Partner-Cross-Workspace on subsequent requests.
      localStorage.removeItem('partnerCrossWorkspaceMode');
      setIsPartnerCrossMode(false);
    } else {
      // Clear workspace for cross-workspace view (System Admin)
      localStorage.removeItem('activeWorkspaceId');
      localStorage.setItem('crossWorkspaceMode', 'true');
      setActiveWorkspace(null);
    }
  }, []);

  // Iter S25 — canonical helper for drilling from Partner rollup
  // (Mode A) into a merchant workspace (Mode B). Single source of
  // truth for the four state changes that must happen atomically:
  //   1. persist activeWorkspaceId to localStorage (via api helper)
  //   2. React state: activeWorkspace = { id }
  //   3. clear crossWorkspaceMode + partnerCrossWorkspaceMode flags
  //   4. dispatch the ``workspaceChanged`` event so any listener that
  //      relies on legacy custom events (sidebar, partner chip) reacts
  //
  // Callers pass ``nextPath`` — a workspace-scoped route the browser
  // then navigates to (using window.location so the workspace context
  // is durable across a refresh). If nextPath is null the caller is
  // responsible for its own navigate() (used by unit tests).
  const enterMerchantWorkspace = useCallback((opts) => {
    const { workspaceId, nextPath, workspaceName } = opts || {};
    if (!workspaceId) {
      return { ok: false, reason: 'missing_workspace_id' };
    }
    setApiActiveWorkspace(workspaceId);
    setActiveWorkspace({ id: workspaceId, name: workspaceName || null });
    localStorage.removeItem('crossWorkspaceMode');
    localStorage.removeItem('partnerCrossWorkspaceMode');
    setIsPartnerCrossMode(false);
    if (previousWorkspaceIdRef.current !== workspaceId) {
      setLastWorkspaceSwitchTime(Date.now());
    }
    previousWorkspaceIdRef.current = workspaceId;
    try {
      window.dispatchEvent(new CustomEvent('partnerModeChanged'));
      window.dispatchEvent(new CustomEvent('workspaceChanged',
        { detail: { workspaceId } }));
    } catch (_e) { /* SSR / non-DOM env */ }
    // Iter S25 Phase 25 UX 3 — surface a small "Now viewing" toast
    // so operators drilling between Partner Rollup and merchant
    // workspaces during a live call always know which workspace
    // they're acting in. Guard on workspaceName presence so a
    // programmatic caller (unit tests, edge flows) that doesn't
    // supply a name doesn't get a "Now viewing undefined" toast.
    if (workspaceName) {
      try {
        toast.success(`Now viewing ${workspaceName}`);
      } catch (_e) { /* SSR / non-DOM env */ }
    }
    return { ok: true, workspaceId, nextPath, workspaceName: workspaceName || null };
  }, []);

  // Get current workspace ID
  const getWorkspaceId = useCallback(() => {
    // Check if cross-workspace mode is enabled
    const crossWorkspaceMode = localStorage.getItem('crossWorkspaceMode') === 'true';
    if (crossWorkspaceMode && user?.role === 'system_admin') {
      return null; // Cross-workspace view
    }
    return activeWorkspace?.id || user?.tenant_id || null;
  }, [activeWorkspace, user]);

  // Convenience computed value for workspaceId (for easier destructuring)
  const crossWorkspaceMode = localStorage.getItem('crossWorkspaceMode') === 'true';
  const workspaceId = (crossWorkspaceMode && user?.role === 'system_admin') ? null : (activeWorkspace?.id || user?.tenant_id || null);
  
  // Convenience computed value for token
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  // Check if workspace was recently switched (within 60 seconds)
  const checkWorkspaceSafetyWarning = useCallback(() => {
    return wasWorkspaceRecentlySwitched();
  }, []);

  // Check if user has a specific role
  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') return user.role === roles;
    return roles.includes(user.role);
  };

  // Check if user has a specific permission based on RBAC matrix
  const hasPermission = (permission) => {
    if (!user) return false;
    const allowedRoles = PERMISSIONS[permission];
    if (!allowedRoles) return false;
    return allowedRoles.includes(user.role);
  };

  // Helper functions for common permission checks
  const canManageUsers = () => hasPermission('invite_users');
  const canApprove = () => hasPermission('approve_case');
  const canOperate = () => hasRole(['meridian_operator', 'meridian_approver']);
  const canUploadEvidence = () => hasPermission('upload_evidence');
  const canImport = () => hasPermission('upload_csv');
  const canValidateImport = () => hasPermission('validate_map_csv');
  const canViewCases = () => hasPermission('view_cases');
  const canEditCase = () => hasPermission('edit_case');
  const canViewAuditLogs = () => hasPermission('view_full_audit');
  const canExportReports = () => hasPermission('export_reports');
  const canViewFinance = () => hasPermission('view_finance_roi');
  const is3PLOperator = () => user?.role === '3pl_operator';
  const isViewer = () => user?.role === 'viewer';
  const isClientAdmin = () => user?.role === 'client_admin';

  // Resolve the active workspace to its FULL record (so consumers get
  // feature_flags, name, workspace_type, etc.) by looking it up in
  // availableWorkspaces. Falls back to the bare `{id}` shape if the
  // list hasn't loaded yet — feature_flag-dependent UI will simply
  // see undefined flags (defaults preserve backwards compatibility).
  const resolvedActiveWorkspace = useMemo(() => {
    if (!activeWorkspace) return null;
    const full = (availableWorkspaces || []).find(
      (w) => w.id === activeWorkspace.id);
    return full || activeWorkspace;
  }, [activeWorkspace, availableWorkspaces]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      hasRole,
      hasPermission,
      canManageUsers,
      canApprove,
      canOperate,
      canUploadEvidence,
      canImport,
      canValidateImport,
      canViewCases,
      canEditCase,
      canViewAuditLogs,
      canExportReports,
      canViewFinance,
      is3PLOperator,
      isViewer,
      isClientAdmin,
      PERMISSIONS,
      // Workspace management
      activeWorkspace: resolvedActiveWorkspace,
      availableWorkspaces,
      switchWorkspace,
      enterMerchantWorkspace,
      getWorkspaceId,
      setAvailableWorkspaces,
      // Safety check for bulk actions
      lastWorkspaceSwitchTime,
      checkWorkspaceSafetyWarning,
      // Convenience properties for easier destructuring
      workspaceId,
      token,
      isCrossWorkspaceMode: crossWorkspaceMode && user?.role === 'system_admin',
      isPartnerCrossMode,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
